import React, { useState } from 'react'
import {
  telephoneMask,
  telephoneUnmask,
  cpfCnpjMask,
  cpfCnpjUnmask,
  cepMask,
  cepUnmask,
} from 'js-essentials-functions'
import { rgMask, clearRgMask } from 'utils/formatRg'

// Context
import { useUser } from 'context/user/user.context'

// Components
import { Input, Button, Select } from 'components'

import { translate } from 'locales/index'

// Styles
import * as S from './styles'

// Services
import { updateUserData } from './services'

// Types
import { IForm } from './interfaces'
import { IAddress } from 'context/user/user.types'

// Options
import { statesBR } from 'utils/statesBR'
import {
  shirtSizes,
  emergencyRelationships,
  genderOptions,
} from 'utils/profileOptions'

const emptyAddress: IAddress = {
  zip_code: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
}

const errorStyle = { border: '1px solid var(--color-error)' }

interface FieldWrapperProps {
  label: string
  error?: string
  children: React.ReactNode
}

const FieldWrapper: React.FunctionComponent<FieldWrapperProps> = ({
  label,
  error,
  children,
}) => (
  <div className="content-input">
    <span className="field-label">{label}</span>
    {children}
    {error && <span className="field-error">{error}</span>}
  </div>
)

const FormProfile: React.FunctionComponent<IForm> = ({ setLoading }) => {
  const { userData, setUserData } = useUser()

  const [errors, setErrors] = useState<Record<string, string>>({})

  const showRqeField = !['CRP', 'CRN'].includes(
    userData?.professional_document_type || ''
  )

  const updateField = (field: string, value: string) => {
    setUserData({ ...userData, [field]: value })
  }

  const updateAddressField = (
    addressField: 'residential_address' | 'professional_address',
    field: keyof IAddress,
    value: string
  ) => {
    setUserData({
      ...userData,
      [addressField]: {
        ...(userData?.[addressField] || emptyAddress),
        [field]: value,
      },
    })
  }

  const handleZipChange = (
    addressField: 'residential_address' | 'professional_address',
    rawValue: string
  ) => {
    const digits = cepUnmask(rawValue)
    updateAddressField(addressField, 'zip_code', digits)

    if (digits.length !== 8) return

    fetch(`https://api.pagar.me/1/zipcodes/${digits}`, { method: 'GET' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return

        setUserData((prev) => ({
          ...prev,
          [addressField]: {
            ...(prev?.[addressField] || emptyAddress),
            zip_code: digits,
            street: data.street || '',
            neighborhood: data.neighborhood || '',
            city: data.city || '',
            state: data.state ? String(data.state).toUpperCase() : '',
          },
        }))
      })
      .catch(() => undefined)
  }

  const updateEmergencyContactField = (field: string, value: string) => {
    setUserData({
      ...userData,
      emergency_contact: {
        ...userData?.emergency_contact,
        name: userData?.emergency_contact?.name || '',
        phone: userData?.emergency_contact?.phone || '',
        relationship: userData?.emergency_contact?.relationship || '',
        [field]: value,
      },
    })
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!userData?.name) newErrors.name = translate('errors.fieldsRequired')
    if (!userData?.email || !emailRegex.test(userData.email))
      newErrors.email = translate('errors.fieldsRequired')
    if (!userData?.rg) newErrors.rg = translate('errors.fieldsRequired')
    if (!userData?.birthdate)
      newErrors.birthdate = translate('errors.fieldsRequired')
    if (!userData?.cellphone)
      newErrors.cellphone = translate('errors.fieldsRequired')

    const residential = userData?.residential_address
    if (!residential?.zip_code)
      newErrors['residential_address.zip_code'] = translate(
        'errors.fieldsRequired'
      )
    if (!residential?.street)
      newErrors['residential_address.street'] = translate(
        'errors.fieldsRequired'
      )
    if (!residential?.number)
      newErrors['residential_address.number'] = translate(
        'errors.fieldsRequired'
      )
    if (!residential?.neighborhood)
      newErrors['residential_address.neighborhood'] = translate(
        'errors.fieldsRequired'
      )
    if (!residential?.city)
      newErrors['residential_address.city'] = translate('errors.fieldsRequired')
    if (!residential?.state)
      newErrors['residential_address.state'] = translate(
        'errors.fieldsRequired'
      )

    if (!userData?.professional_document_number)
      newErrors.professional_document_number = translate(
        'errors.fieldsRequired'
      )
    if (!userData?.professional_document_uf)
      newErrors.professional_document_uf = translate('errors.fieldsRequired')

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) {
      return
    }

    updateUserData(userData, setLoading)
  }

  return (
    <S.FormContent>
      <h3 className="section-title">
        {translate('profile.sectionPersonalData')}
      </h3>
      <FieldWrapper label={translate('profile.name')} error={errors.name}>
        <Input
          name="nome"
          placeholder={translate('profile.name')}
          value={userData.name}
          type="text"
          styles={errors.name ? errorStyle : undefined}
          onChange={(e) => updateField('name', e.currentTarget.value)}
        />
      </FieldWrapper>
      <FieldWrapper label={translate('profile.email')} error={errors.email}>
        <Input
          name="email"
          placeholder={translate('profile.email')}
          value={userData.email}
          type="email"
          styles={errors.email ? errorStyle : undefined}
          onChange={(e) => updateField('email', e.currentTarget.value)}
        />
      </FieldWrapper>
      <div className="content-inputs two-columns">
        <FieldWrapper
          label={translate('profile.cellphone')}
          error={errors.cellphone}
        >
          <Input
            name="celular"
            placeholder={translate('profile.cellphone')}
            maxLength={15}
            value={userData?.cellphone ? telephoneMask(userData.cellphone) : ''}
            type="text"
            styles={errors.cellphone ? errorStyle : undefined}
            onChange={(e) =>
              updateField('cellphone', telephoneUnmask(e.currentTarget.value))
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.document')}>
          <Input
            name="document"
            placeholder={translate('profile.document')}
            maxLength={14}
            value={userData.document ? cpfCnpjMask(userData.document) : ''}
            type="text"
            disabled
            onChange={(e) =>
              updateField('document', cpfCnpjUnmask(e.currentTarget.value))
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs two-columns">
        <FieldWrapper label={translate('profile.rg')} error={errors.rg}>
          <Input
            name="rg"
            placeholder={translate('profile.rg')}
            maxLength={14}
            value={userData.rg ? rgMask(userData.rg) : ''}
            type="text"
            styles={errors.rg ? errorStyle : undefined}
            onChange={(e) =>
              updateField('rg', clearRgMask(e.currentTarget.value))
            }
          />
        </FieldWrapper>
        <FieldWrapper
          label={translate('profile.birthdate')}
          error={errors.birthdate}
        >
          <Input
            name="birthdate"
            placeholder={translate('profile.birthdate')}
            value={userData.birthdate || ''}
            type="date"
            styles={errors.birthdate ? errorStyle : undefined}
            onChange={(e) => updateField('birthdate', e.currentTarget.value)}
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs two-columns">
        <FieldWrapper label={translate('profile.gender')}>
          <Select
            name="gender"
            placeholder={translate('profile.gender')}
            list={genderOptions}
            value={userData.gender || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateField('gender', e.currentTarget.value)
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.shirtSize')}>
          <Select
            name="shirt_size"
            placeholder={translate('profile.shirtSize')}
            list={shirtSizes}
            value={userData.shirt_size || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateField('shirt_size', e.currentTarget.value)
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs two-columns">
        <FieldWrapper
          label={translate('profile.zipCode')}
          error={errors['residential_address.zip_code']}
        >
          <Input
            name="residential-zip"
            placeholder={translate('profile.zipCode')}
            maxLength={9}
            value={
              userData.residential_address?.zip_code
                ? cepMask(userData.residential_address.zip_code)
                : ''
            }
            type="text"
            styles={
              errors['residential_address.zip_code'] ? errorStyle : undefined
            }
            onChange={(e) =>
              handleZipChange('residential_address', e.currentTarget.value)
            }
          />
        </FieldWrapper>
        <FieldWrapper
          label={translate('profile.city')}
          error={errors['residential_address.city']}
        >
          <Input
            name="residential-city"
            placeholder={translate('profile.city')}
            value={userData.residential_address?.city || ''}
            type="text"
            styles={errors['residential_address.city'] ? errorStyle : undefined}
            onChange={(e) =>
              updateAddressField(
                'residential_address',
                'city',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs address-columns">
        <FieldWrapper
          label={translate('profile.street')}
          error={errors['residential_address.street']}
        >
          <Input
            name="residential-street"
            placeholder={translate('profile.street')}
            value={userData.residential_address?.street || ''}
            type="text"
            styles={
              errors['residential_address.street'] ? errorStyle : undefined
            }
            onChange={(e) =>
              updateAddressField(
                'residential_address',
                'street',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper
          label={translate('profile.number')}
          error={errors['residential_address.number']}
        >
          <Input
            name="residential-number"
            placeholder={translate('profile.number')}
            value={userData.residential_address?.number || ''}
            type="text"
            styles={
              errors['residential_address.number'] ? errorStyle : undefined
            }
            onChange={(e) =>
              updateAddressField(
                'residential_address',
                'number',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.complement')}>
          <Input
            name="residential-complement"
            placeholder={translate('profile.complement')}
            value={userData.residential_address?.complement || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'residential_address',
                'complement',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs two-columns">
        <FieldWrapper
          label={translate('profile.neighborhood')}
          error={errors['residential_address.neighborhood']}
        >
          <Input
            name="residential-neighborhood"
            placeholder={translate('profile.neighborhood')}
            value={userData.residential_address?.neighborhood || ''}
            type="text"
            styles={
              errors['residential_address.neighborhood']
                ? errorStyle
                : undefined
            }
            onChange={(e) =>
              updateAddressField(
                'residential_address',
                'neighborhood',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper
          label={translate('profile.state')}
          error={errors['residential_address.state']}
        >
          <Select
            name="residential-state"
            placeholder={translate('profile.state')}
            list={statesBR}
            value={userData.residential_address?.state || ''}
            styles={
              errors['residential_address.state'] ? errorStyle : undefined
            }
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateAddressField(
                'residential_address',
                'state',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>

      <h3 className="section-title">
        {translate('profile.sectionProfessionalData')}
      </h3>
      <div className="content-inputs three-columns">
        <FieldWrapper label={translate('profile.professionalDocumentType')}>
          <Input
            name="professional_document_type"
            placeholder={translate('profile.professionalDocumentType')}
            value={userData.professional_document_type || ''}
            type="text"
            disabled
            onChange={() => {}}
          />
        </FieldWrapper>
        <FieldWrapper
          label={`${translate('profile.professionalDocumentNumber')}${
            userData.professional_document_type
              ? ` (${userData.professional_document_type})`
              : ''
          }`}
          error={errors.professional_document_number}
        >
          <Input
            name="professional_document_number"
            placeholder={translate('profile.professionalDocumentNumber')}
            value={userData.professional_document_number || ''}
            type="text"
            styles={
              errors.professional_document_number ? errorStyle : undefined
            }
            onChange={(e) =>
              updateField('professional_document_number', e.currentTarget.value)
            }
          />
        </FieldWrapper>
        <FieldWrapper
          label={translate('profile.state')}
          error={errors.professional_document_uf}
        >
          <Select
            name="professional_document_uf"
            placeholder={translate('profile.state')}
            list={statesBR}
            value={userData.professional_document_uf || ''}
            styles={errors.professional_document_uf ? errorStyle : undefined}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateField('professional_document_uf', e.currentTarget.value)
            }
          />
        </FieldWrapper>
      </div>
      {showRqeField && (
        <FieldWrapper label={translate('profile.rqeOptional')}>
          <Input
            name="rqe"
            placeholder={translate('profile.rqe')}
            value={userData.rqe || ''}
            type="text"
            onChange={(e) => updateField('rqe', e.currentTarget.value)}
          />
        </FieldWrapper>
      )}
      <FieldWrapper label={translate('profile.professionalPhone')}>
        <Input
          name="professional_phone"
          placeholder={translate('profile.professionalPhone')}
          maxLength={15}
          value={
            userData?.professional_phone
              ? telephoneMask(userData.professional_phone)
              : ''
          }
          type="text"
          onChange={(e) =>
            updateField(
              'professional_phone',
              telephoneUnmask(e.currentTarget.value)
            )
          }
        />
      </FieldWrapper>

      <div className="content-inputs two-columns">
        <FieldWrapper label={translate('profile.zipCode')}>
          <Input
            name="professional-zip"
            placeholder={translate('profile.zipCode')}
            maxLength={9}
            value={
              userData.professional_address?.zip_code
                ? cepMask(userData.professional_address.zip_code)
                : ''
            }
            type="text"
            onChange={(e) =>
              handleZipChange('professional_address', e.currentTarget.value)
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.city')}>
          <Input
            name="professional-city"
            placeholder={translate('profile.city')}
            value={userData.professional_address?.city || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'professional_address',
                'city',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs address-columns">
        <FieldWrapper label={translate('profile.street')}>
          <Input
            name="professional-street"
            placeholder={translate('profile.street')}
            value={userData.professional_address?.street || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'professional_address',
                'street',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.number')}>
          <Input
            name="professional-number"
            placeholder={translate('profile.number')}
            value={userData.professional_address?.number || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'professional_address',
                'number',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.complement')}>
          <Input
            name="professional-complement"
            placeholder={translate('profile.complement')}
            value={userData.professional_address?.complement || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'professional_address',
                'complement',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>
      <div className="content-inputs two-columns">
        <FieldWrapper label={translate('profile.neighborhood')}>
          <Input
            name="professional-neighborhood"
            placeholder={translate('profile.neighborhood')}
            value={userData.professional_address?.neighborhood || ''}
            type="text"
            onChange={(e) =>
              updateAddressField(
                'professional_address',
                'neighborhood',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.state')}>
          <Select
            name="professional-state"
            placeholder={translate('profile.state')}
            list={statesBR}
            value={userData.professional_address?.state || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateAddressField(
                'professional_address',
                'state',
                e.currentTarget.value
              )
            }
          />
        </FieldWrapper>
      </div>

      <h3 className="section-title">{translate('profile.emergencyContact')}</h3>
      <FieldWrapper label={translate('profile.emergencyContactName')}>
        <Input
          name="emergency_contact_name"
          placeholder={translate('profile.emergencyContactName')}
          value={userData?.emergency_contact?.name || ''}
          type="text"
          onChange={(e) =>
            updateEmergencyContactField('name', e.currentTarget.value)
          }
        />
      </FieldWrapper>
      <div className="content-inputs two-columns">
        <FieldWrapper label={translate('profile.emergencyContactPhone')}>
          <Input
            name="emergency_contact_phone"
            placeholder={translate('profile.emergencyContactPhone')}
            maxLength={15}
            value={
              userData?.emergency_contact?.phone
                ? telephoneMask(userData.emergency_contact.phone)
                : ''
            }
            type="text"
            onChange={(e) =>
              updateEmergencyContactField(
                'phone',
                telephoneUnmask(e.currentTarget.value)
              )
            }
          />
        </FieldWrapper>
        <FieldWrapper label={translate('profile.relationship')}>
          <Select
            name="emergency_contact_relationship"
            placeholder={translate('profile.relationship')}
            list={emergencyRelationships}
            value={userData?.emergency_contact?.relationship || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              updateEmergencyContactField('relationship', e.currentTarget.value)
            }
          />
        </FieldWrapper>
      </div>

      <div className="area-button">
        <Button
          type="button"
          color="primary"
          size="small"
          onClick={handleSave}
        >
          {translate('profile.save')}
        </Button>
      </div>
    </S.FormContent>
  )
}

export default FormProfile
